import { Badge, Collapse, Header } from '@breward/ui';
import { useTranslation } from 'react-i18next';
import { sliderRangeText } from '../../../utils/intensityScale';
import { BeerGuidelines } from '../types';
import { DescriptorBadge } from './DescriptorBadge';

interface JudgeGuidelinesProps {
    guidelinesSectionKey: string;
    gl?: BeerGuidelines;
    schemaData: any;
}

export const JudgeGuidelines = ({
    guidelinesSectionKey,
    gl,
    schemaData,
}: JudgeGuidelinesProps) => {
    const [t, i18n] = useTranslation();

    const getSection = (key: string) => {
        return schemaData?.data?.sections?.find((section: any) => section?.key === key);
    };

    const getSubsection = (sectionKey: string, key: string) => {
        return getSection(sectionKey)?.subsections?.find(
            (subsection: any) => subsection?.key === key,
        );
    };

    const getField = (sectionKey: string, subsectionKey: string, key: string) => {
        return getSubsection(sectionKey, subsectionKey)?.fields?.find(
            (field: any) => field?.key === key,
        );
    };

    const renderGuidelineRule = (rule: any, field: any) => {
        switch (rule?.ruleType) {
            case 'range': {
                const rangeText = sliderRangeText(
                    t,
                    field?.settings,
                    Number(rule?.settings?.minValue),
                    Number(rule?.settings?.maxValue),
                    i18n?.language,
                );
                return (
                    <div className="flex items-center gap-1.5">
                        <div className="flex items-center gap-0.5">
                            {Array.from({ length: field?.settings?.maxValue - field?.settings?.minValue + 1 }, (_, i) => {
                                const value = field?.settings?.minValue + i;
                                const isInRange = value >= rule?.settings?.minValue && value <= rule?.settings?.maxValue;
                                return (
                                    <div
                                        key={value}
                                        className={`flex h-4.5 w-4.5 items-center justify-center rounded-full text-[9px] font-bold transition-all duration-250 ${isInRange
                                            ? 'bg-primary-500 text-white shadow-sm'
                                            : 'bg-primary-100 text-primary-400 dark:bg-primary-800 dark:text-primary-600'
                                            }`}
                                    >
                                        {value}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="text-[10px] text-primary-600 dark:text-primary-400 font-semibold">
                            ({rule?.settings?.minValue} - {rule?.settings?.maxValue})
                            {rangeText && <span className="ml-1 font-normal">{rangeText}</span>}
                        </div>
                    </div>
                );
            }

            case 'inclusion':
                return (
                    <div className="flex flex-wrap gap-1">
                        {rule?.settings?.values?.map((val: string, index: number) => (
                            <DescriptorBadge key={index} value={val} />
                        ))}
                    </div>
                );

            case 'boolean':
                return (
                    <Badge
                        label={
                            <div className="flex items-center gap-1">
                                <div className={`i-ph-${rule?.settings?.value ? 'check' : 'x'}-bold h-3 w-3`} />
                                <span className="text-[10px] font-medium">
                                    {rule?.settings?.value ? t('global:shared.yes', 'Yes') : t('global:shared.no', 'No')}
                                </span>
                            </div>
                        }
                        type={rule?.settings?.value ? "success" : "error"}
                        className="text-xs"
                    />
                );

            case 'balance':
                const ratios = rule?.settings?.ratios || [];
                const labels = field?.settings?.values || [];
                const total = ratios.reduce((sum: number, ratio: number) => sum + ratio, 0);
                const colors = ['bg-primary-500', 'bg-accent-200', 'bg-secondary-500'];

                return (
                    <div className="space-y-1 w-full max-w-[200px]">
                        <div className="flex h-3.5 rounded overflow-hidden border border-primary-200 dark:border-primary-700">
                            {ratios.map((ratio: number, index: number) => {
                                const percentage = total > 0 ? (ratio / total) * 100 : 0;
                                return (
                                    <div
                                        key={index}
                                        className={`${colors[index % colors.length]} h-full`}
                                        style={{ width: `${percentage}%` }}
                                        title={`${labels[index] || `Segment ${index + 1}`}: ${ratio}`}
                                    />
                                );
                            })}
                        </div>
                        <div className="flex flex-wrap gap-x-2 gap-y-0.5 justify-end">
                            {labels.map((label: string, index: number) => (
                                <span key={index} className="text-[9px] text-primary-600 dark:text-primary-400 flex items-center gap-1 font-semibold">
                                    <span className={`w-1.5 h-1.5 rounded-sm ${colors[index % colors.length]}`} />
                                    {label}: {ratios[index] || 0}
                                </span>
                            ))}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const guidSectionData = gl?.sections?.find(s => s.key === guidelinesSectionKey);

    if (!guidSectionData) return null;

    return (
        <Collapse
            title={t('Pages:ParticipantWeb.ShowStylePage.JudgeGuidelines.label', 'Wytyczne sędziowskie')}
            variant="surface"
            density="dense"
            className="mt-3"
        >
            <div className="space-y-3 mt-1.5">
                {guidSectionData.subsections?.map((subsection: any) => {
                    const schemaSubsection = getSubsection(guidelinesSectionKey, subsection.key);
                    return (
                        <div key={subsection.key} className="border-l-2 border-primary-200 dark:border-primary-800 pl-3 space-y-2">
                            <Header
                                variant="card"
                                density="compact"
                                title={schemaSubsection?.label || subsection.key}
                                className="border-b border-primary-200/20 dark:border-primary-800/25 pb-0.5"
                            />
                            <div className="space-y-1.5">
                                {subsection.rules?.map((rule: any) => {
                                    const field = getField(guidelinesSectionKey, subsection.key, rule.key);
                                    return (
                                        <div
                                            key={rule.key}
                                            className="bg-primary-50/30 dark:bg-primary-900/5 rounded-lg py-1.5 px-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 border border-solid border-primary-100/30 dark:border-primary-900/5"
                                        >
                                            <div className="text-xs font-medium text-primary-750 dark:text-primary-300">
                                                {field?.label || rule.key}
                                            </div>
                                            <div className="flex-shrink-0">
                                                {renderGuidelineRule(rule, field)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Collapse>
    );
};
